import { Link } from 'react-router';
import type { AppFile } from '../api';
import { RELAY_LINK, RELAY_STUDIO, STREVIX_OBS, hintFiles, isCli, osShort, useApps } from '../apps';
import { AppIcon, DownloadLink, sizeOf } from '../components/apps-ui';

/*
 * Links to the apps where they help (docs/apps-downloads.md 3.5, artboard
 * Apps-3-EntryPoints). Nothing renders for an app without published builds.
 */

/**
 * Strevix for OBS: the plugin sets OBS up itself (sign in, nothing to paste).
 * "row" in the OBS settings popover, "card" in setup step 1. The user's
 * installer as the button; another computer gets every one; a phone the page.
 */
export function ObsPluginHint({ variant = 'row' }: { variant?: 'row' | 'card' }) {
  const apps = useApps();
  const plugin = apps.state === 'ready' ? apps.app(STREVIX_OBS) : undefined;
  if (!plugin) return null;
  const known = apps.os === 'macos' || apps.os === 'windows';
  const { mine, others } = hintFiles(plugin, apps.os);
  const buttons = !known ? (
    <Link to={`/apps#${plugin.id}`} className="flex min-h-[34px] items-center whitespace-nowrap text-[13px] text-accent no-underline max-md:min-h-[44px]">
      Get it →
    </Link>
  ) : mine ? (
    <>
      <DownloadLink file={mine} primary size="sm" testId="apps-hint-obs-plugin-download">
        {variant === 'card' ? `Download for ${osShort(mine.os)}` : osShort(mine.os)} · {sizeOf(mine)}
      </DownloadLink>
      {variant === 'card' && (
        <Link to={`/apps#${plugin.id}`} className="flex min-h-[34px] items-center whitespace-nowrap text-[13px] text-accent no-underline max-md:min-h-[44px]">
          How to install →
        </Link>
      )}
    </>
  ) : (
    others.map((f) => (
      <DownloadLink key={f.url} file={f} size="sm">{osShort(f.os)} · {sizeOf(f)}</DownloadLink>
    ))
  );
  if (variant === 'card') {
    return (
      <div data-testid="apps-hint-obs-plugin" className="flex items-start gap-3 rounded-md border border-line-2 p-3">
        <span className="text-accent"><AppIcon app={plugin.id} /></span>
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <span className="text-sm text-text">Or let OBS set itself up: install <b className="font-semibold">{plugin.name}</b>.</span>
          <span className="text-[13px] text-text-soft">Sign in from its dock in OBS, and OBS streams here: no address or key to paste. Needs OBS 30 or newer.</span>
          <div className="flex flex-wrap items-center gap-2">{buttons}</div>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="apps-hint-obs-plugin" className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
      <span className="text-text-soft"><AppIcon app={plugin.id} /></span>
      <span className="min-w-0 flex-1 text-[13px] text-text-soft">
        Skip the setup: <b className="font-semibold text-text">{plugin.name}</b> sets OBS up when you sign in.
      </span>
      {buttons}
    </div>
  );
}

/** Bottom of the OBS settings popover / phone source widget. */
export function ObsAppsHints() {
  const apps = useApps();
  if (apps.state !== 'ready') return null;
  const relay = apps.app(RELAY_STUDIO);
  const ws = apps.app(RELAY_LINK);
  const plugin = apps.app(STREVIX_OBS);
  if (!relay && !ws && !plugin) return null;
  const known = apps.os === 'macos' || apps.os === 'windows';
  const { mine, others } = hintFiles(relay, apps.os);
  return (
    <div className="flex flex-col gap-2 border-t border-dashed border-line-2 pt-2.5">
      {plugin && <ObsPluginHint />}
      {relay && (
        <div data-testid="apps-hint-obs" className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
          <span className="text-text-soft"><AppIcon app={relay.id} /></span>
          <span className="min-w-0 flex-1 text-[13px] text-text-soft">
            No OBS? Stream with <b className="font-semibold text-text">{relay.name}</b>.
          </span>
          {!known ? (
            <Link to="/apps" className="flex min-h-[34px] items-center whitespace-nowrap text-[13px] text-accent no-underline max-md:min-h-[44px]">
              Get it →
            </Link>
          ) : mine ? (
            <DownloadLink file={mine} primary size="sm">{osShort(mine.os)} · {sizeOf(mine)}</DownloadLink>
          ) : (
            others.map((f) => (
              <DownloadLink key={f.url} file={f} size="sm">{osShort(f.os)} · {sizeOf(f)}</DownloadLink>
            ))
          )}
        </div>
      )}
      {ws && (
        <div data-testid="apps-hint-obs-wsbridge" className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
          <span className="text-text-soft"><AppIcon app={ws.id} /></span>
          <span className="min-w-0 flex-1 text-[13px] text-text-soft">
            OBS can’t connect? Try <b className="font-semibold text-text">{ws.name}</b>.
          </span>
          <Link to={`/apps#${ws.id}`} className="flex min-h-[34px] items-center whitespace-nowrap text-[13px] text-accent no-underline max-md:min-h-[44px]">
            How it works →
          </Link>
        </div>
      )}
    </div>
  );
}

/** " · command line" after the OS of a build without a window (wsbridge for Mac). */
const cli = (f: AppFile) => (isCli(f) ? ' · command line' : '');

/** Setup step 1, no stream for 45 s: the network may block streaming ports (Relay Link). */
export function RelayLinkHint() {
  const apps = useApps();
  const ws = apps.state === 'ready' ? apps.app(RELAY_LINK) : undefined;
  if (!ws) return null;
  const known = apps.os === 'macos' || apps.os === 'windows';
  const { mine, others } = hintFiles(ws, apps.os);
  return (
    <div data-testid="apps-hint-wsbridge" className="flex items-start gap-3 rounded-md border border-warn/[.45] p-3">
      <span className="text-warn"><AppIcon app={ws.id} /></span>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <span className="text-sm text-text">Your network may block streaming ports.</span>
        <span className="text-[13px] text-text-soft">
          {ws.name} sends OBS over port 443, like a website. Then use <span className="break-all font-mono">rtmp://127.0.0.1:1935/live</span> in OBS.
        </span>
        <div className="flex flex-wrap gap-2">
          {!known ? (
            <Link to={`/apps#${ws.id}`} className="flex min-h-[34px] items-center text-[13px] text-accent no-underline max-md:min-h-[44px]">
              Get {ws.name} on your computer →
            </Link>
          ) : (
            <>
              {mine && <DownloadLink file={mine} primary size="sm">{ws.name} for {osShort(mine.os)}{cli(mine)} · {sizeOf(mine)}</DownloadLink>}
              {others.map((f) => (
                <DownloadLink key={f.url} file={f} size="sm">
                  {mine ? `${osShort(f.os)}${cli(f)}` : `${ws.name} for ${osShort(f.os)}${cli(f)} · ${sizeOf(f)}`}
                </DownloadLink>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
